// src/composables/useWebAuthn.js
// WebAuthn (Passkey) utilities for registration and authentication

import { ref } from 'vue';

export function useWebAuthn() {
  const isSupported = ref(typeof window !== 'undefined' && window.PublicKeyCredential !== undefined);
  const isLoading = ref(false);
  const error = ref(null);

  // Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
  const checkPlatformAuthenticator = async () => {
    if (!isSupported.value) return false;
    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  };

  // Check if conditional mediation (autofill) is available
  const checkConditionalMediation = async () => {
    if (!isSupported.value) return false;
    try {
      return await window.PublicKeyCredential.isConditionalMediationAvailable?.() ?? false;
    } catch {
      return false;
    }
  };

  // Convert base64url to ArrayBuffer
  const base64urlToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(base64 + padding);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Convert ArrayBuffer to base64url
  const bufferToBase64url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  // Register a new passkey (called during enrollment)
  const registerPasskey = async (options) => {
    if (!isSupported.value) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    // Validate options
    if (!options || typeof options !== 'object') {
      throw new Error('Invalid registration options received from server');
    }
    if (!options.challenge || typeof options.challenge !== 'string') {
      throw new Error('Invalid challenge received from server');
    }
    if (!options.rp || !options.rp.name) {
      throw new Error('Invalid relying party information');
    }
    if (!options.user || !options.user.id || !options.user.name) {
      throw new Error('Invalid user information');
    }

    isLoading.value = true;
    error.value = null;

    try {
      // Convert server options to proper format
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: {
          name: options.rp.name,
          id: options.rp.id,
        },
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams || [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        timeout: options.timeout || 60000,
        attestation: options.attestation || 'none',
        authenticatorSelection: options.authenticatorSelection || {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: 'preferred',
        },
        excludeCredentials: (options.excludeCredentials || []).map((cred) => ({
          type: 'public-key',
          id: base64urlToBuffer(cred.id),
          transports: cred.transports,
        })),
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      // Format response for server
      const response = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
        },
      };

      if (credential.response.getTransports) {
        response.transports = credential.response.getTransports();
      }

      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Authenticate with a passkey (called during login)
  const authenticatePasskey = async (options, conditional = false) => {
    if (!isSupported.value) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    // Validate options
    if (!options || typeof options !== 'object') {
      throw new Error('No passkeys available for this account');
    }
    if (!options.challenge || typeof options.challenge !== 'string') {
      throw new Error('No passkeys available for this account');
    }

    isLoading.value = true;
    error.value = null;

    try {
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout || 60000,
        rpId: options.rpId,
        userVerification: options.userVerification || 'preferred',
        allowCredentials: (options.allowCredentials || []).map((cred) => ({
          type: 'public-key',
          id: base64urlToBuffer(cred.id),
          transports: cred.transports,
        })),
      };

      const credentialOptions = {
        publicKey: publicKeyOptions,
      };

      // Enable conditional mediation for autofill UI
      if (conditional) {
        credentialOptions.mediation = 'conditional';
      }

      const credential = await navigator.credentials.get(credentialOptions);

      // Format response for server
      return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle
            ? bufferToBase64url(credential.response.userHandle)
            : null,
        },
      };
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Abort any ongoing WebAuthn operation
  const abortController = ref(null);

  const abort = () => {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
  };

  return {
    isSupported,
    isLoading,
    error,
    checkPlatformAuthenticator,
    checkConditionalMediation,
    registerPasskey,
    authenticatePasskey,
    abort,
    base64urlToBuffer,
    bufferToBase64url,
  };
}
